/**
 * Sense — slices 59 and 60. Every read is parsed against the shared contract by
 * `apiRequest`, so a response that drifted from `pressure.ts` or `outcomes.ts`
 * fails here rather than drawing a band or a stage from a field that is not
 * there.
 *
 * Pressure writes nothing. "Rest people at Critical" is not a Sense call: it
 * rides on the campaign's own schedule request (`restCritical`), because resting
 * is a decision about who that one campaign is built for, made where it is
 * scheduled.
 *
 * Outcomes write: an outcome type, a declared count, a list of who converted.
 * The list goes up as multipart under the field name `file` — part of the
 * contract, as it is for a dataset upload.
 *
 * Slice 70: Today's `flow_draft` action creates a DRAFT flow from a template
 * (`POST /flows`, {@link draftFlowForFinding}). It lives here rather than
 * being borrowed from `lib/api/flows.ts` because it is Sense's one write into
 * another pillar and its shape is fixed by the finding — template and segment,
 * never a graph — so the body is built and parsed here, against the shared
 * `createFlowRequestSchema`, and cannot carry a launch.
 */
import {
  FLOW_TEMPLATES,
  createFlowRequestSchema,
  declaredOutcomeSchema,
  flowResponseSchema,
  funnelSchema,
  holdoutReadoutSchema,
  postbackKeyCreatedSchema,
  postbackKeyStatusSchema,
  senseActionSchema,
  senseActionsSchema,
  todaySchema,
  outcomeTypeSchema,
  outcomeUploadSchema,
  outcomesOverviewSchema,
  segmentPressureSchema,
  workspacePressureSchema,
  type Channel,
  type CreateOutcomeTypeRequest,
  type DeclaredOutcome,
  type FlowDetail,
  type FlowTemplateId,
  type Funnel,
  type HoldoutReadout,
  type PostbackKeyCreated,
  type PostbackKeyStatus,
  type RecordSenseActionRequest,
  type SenseAction,
  type SenseActions,
  type Today,
  type OutcomeType,
  type OutcomeUpload,
  type OutcomesOverview,
  type RecordDeclaredOutcomeRequest,
  type SegmentPressure,
  type UpdateOutcomeTypeRequest,
  type WorkspacePressure,
} from '@wend/shared';

import { apiRequest } from './client';

/** `GET /sense/pressure` — the whole workspace, over the last 30 days. */
export async function fetchWorkspacePressure(signal?: AbortSignal): Promise<WorkspacePressure> {
  return apiRequest({ path: '/sense/pressure', schema: workspacePressureSchema, signal });
}

/** `GET /sense/pressure/segments/:id` — what a send to this segment on this channel would do. */
export async function fetchSegmentPressure(
  segmentId: string,
  channel: Channel,
  signal?: AbortSignal,
): Promise<SegmentPressure> {
  return apiRequest({
    path: `/sense/pressure/segments/${encodeURIComponent(segmentId)}`,
    query: { channel },
    schema: segmentPressureSchema,
    signal,
  });
}

/** `GET /sense/funnel` — five stages per channel, each measured or saying why not. */
export async function fetchFunnel(signal?: AbortSignal): Promise<Funnel> {
  return apiRequest({ path: '/sense/funnel', schema: funnelSchema, signal });
}

/** `GET /sense/outcomes` — the types, and the most recent uploads and declared counts. */
export async function fetchOutcomes(signal?: AbortSignal): Promise<OutcomesOverview> {
  return apiRequest({ path: '/sense/outcomes', schema: outcomesOverviewSchema, signal });
}

export async function createOutcomeType(body: CreateOutcomeTypeRequest): Promise<OutcomeType> {
  return apiRequest({ path: '/sense/outcomes/types', method: 'POST', body, schema: outcomeTypeSchema });
}

export async function updateOutcomeType(outcomeTypeId: string, body: UpdateOutcomeTypeRequest): Promise<OutcomeType> {
  return apiRequest({
    path: `/sense/outcomes/types/${encodeURIComponent(outcomeTypeId)}`,
    method: 'PATCH',
    body,
    schema: outcomeTypeSchema,
  });
}

export async function recordDeclaredOutcome(body: RecordDeclaredOutcomeRequest): Promise<DeclaredOutcome> {
  return apiRequest({ path: '/sense/outcomes/declared', method: 'POST', body, schema: declaredOutcomeSchema });
}

/** `POST /sense/outcomes/upload` — a CSV or XLSX of who converted, against one type. */
export async function uploadOutcomes(file: File, outcomeTypeId: string): Promise<OutcomeUpload> {
  const form = new FormData();
  form.append('file', file, file.name);
  form.append('outcomeTypeId', outcomeTypeId);
  return apiRequest({ path: '/sense/outcomes/upload', method: 'POST', body: form, schema: outcomeUploadSchema });
}

/** `GET /sense/holdouts/campaigns/:id` — slice 61, sent against held back. */
export async function fetchHoldoutReadout(campaignId: string, signal?: AbortSignal): Promise<HoldoutReadout> {
  return apiRequest({
    path: `/sense/holdouts/campaigns/${encodeURIComponent(campaignId)}`,
    schema: holdoutReadoutSchema,
    signal,
  });
}

/** `GET /sense/today` — slice 62, the ranked queue. */
export async function fetchToday(signal?: AbortSignal): Promise<Today> {
  return apiRequest({ path: '/sense/today', schema: todaySchema, signal });
}

/** `POST /sense/actions` — take or dismiss a finding. Only the fingerprint and the decision travel. */
export async function recordSenseAction(body: RecordSenseActionRequest): Promise<SenseAction> {
  return apiRequest({ path: '/sense/actions', method: 'POST', body, schema: senseActionSchema });
}

/** `GET /sense/actions` — the action ledger. */
export async function fetchSenseActions(signal?: AbortSignal): Promise<SenseActions> {
  return apiRequest({ path: '/sense/actions', schema: senseActionsSchema, signal });
}

/** `GET /sense/postback-key` — slice 63, whether the workspace has a key. Never the key. */
export async function fetchPostbackKey(signal?: AbortSignal): Promise<PostbackKeyStatus> {
  return apiRequest({ path: '/sense/postback-key', schema: postbackKeyStatusSchema, signal });
}

/** `POST /sense/postback-key` — a new key, replacing any; shown in this answer only. */
export async function createPostbackKey(): Promise<PostbackKeyCreated> {
  return apiRequest({ path: '/sense/postback-key', method: 'POST', body: {}, schema: postbackKeyCreatedSchema });
}

/**
 * `POST /flows` — slice 70, Today's "Draft a flow". A draft from the finding's
 * template, with the finding's segment as its audience when the finding names
 * one — today's detectors name none, so the person chooses who it is for in the
 * editor. Creating never launches; the person lands in the editor and launches it.
 */
export async function draftFlowForFinding(template: FlowTemplateId, segmentId: string | null): Promise<FlowDetail> {
  const body = createFlowRequestSchema.parse({
    name: FLOW_TEMPLATES[template].label,
    template,
    audienceSegmentId: segmentId,
  });
  const response = await apiRequest({ path: '/flows', method: 'POST', body, schema: flowResponseSchema });
  return response.flow;
}
